<script setup lang="ts">
import { ref } from "vue";
import { useTranslation } from "i18next-vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
const props = defineProps<{
  loading: boolean;
}>();

const emit = defineEmits<{
  send: [text: string];
}>();

const { t } = useTranslation();
const inputText = ref("");

function handleSend() {
  const text = inputText.value.trim();
  if (!text || props.loading) return;
  emit("send", text);
  inputText.value = "";
}

function handleKeydown(event: Event | KeyboardEvent) {
  if (!("key" in event)) return;
  if (event.key === "Enter" && !event.shiftKey) {
    event.preventDefault();
    handleSend();
  }
}
</script>

<template>
  <div class="chatbot-input d-flex gap-2 align-items-end">
    <div class="chatbot-input-wrapper">
      <el-input
        v-model="inputText"
        type="textarea"
        :autosize="{ minRows: 1, maxRows: 4 }"
        :placeholder="t('help.chatbot.placeholder')"
        :disabled="loading"
        @keydown="handleKeydown"
      />
    </div>
    <button
      class="chatbot-send-btn"
      :class="{ active: inputText.trim() && !loading }"
      :disabled="!inputText.trim() || loading"
      @click="handleSend"
    >
      <PhosphorIcon
        v-if="!loading"
        name="paper-plane-tilt"
        :size="18"
        weight="fill"
      />
      <PhosphorIcon
        v-else
        name="spinner"
        :size="18"
        weight="bold"
        class="spinning"
      />
    </button>
  </div>
</template>

<style lang="scss" scoped>
.chatbot-input {
  padding: 0;
}

.chatbot-input-wrapper {
  flex: 1;
  min-width: 0;

  :deep(.el-textarea__inner) {
    border-radius: 20px;
    padding: 10px 16px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    transition: border-color 0.2s;
    resize: none;

    &:focus {
      border-color: #93c5fd;
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
    }
  }
}

.chatbot-send-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background-color: #cbd5e1;
  color: white;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &.active {
    background-color: #2563eb;

    &:hover {
      background-color: #1d4ed8;
      transform: scale(1.05);
    }
  }

  &:disabled {
    cursor: not-allowed;
  }
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
